import { inject, Injectable } from '@angular/core'
import { AddressSelectItem, GeocodingSuggest, Place } from './map-model'
import { environment } from '../../../../environments/environment'
import { HttpClient, HttpContext } from '@angular/common/http'
import {
  ALLOWED_COUNTRY,
  ALLOWED_COUNTRY_CODE,
  AWS_MAP_PLACES,
  INTENDED_USE,
  LATITUDE_RANGE,
  LONGITUDE_RANGE,
  SEARCH_LANGUAGE,
} from './map-config'
import { map, Observable } from 'rxjs'
import { USE_API_KEY } from '../../../shared/interceptors/api-key.interceptor'
import { LngLat } from 'maplibre-gl'
import { SPINNER_ENABLED } from '../../../shared/interceptors/loading-spinner.interceptor'

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly httpClient = inject(HttpClient)
  private readonly context = {
    context: new HttpContext().set(USE_API_KEY, false).set(SPINNER_ENABLED, false),
  }

  // find place by coordinates (Germany only)
  reverseGeocoding(lngLat: LngLat): Observable<AddressSelectItem[]> {
    const payload = {
      QueryPosition: [lngLat.lng, lngLat.lat],
      MaxResults: 5,
      IntendedUse: INTENDED_USE,
      Language: SEARCH_LANGUAGE,
    }
    return this.httpClient
      .post<{
        ResultItems: Place[]
      }>(`${AWS_MAP_PLACES}/reverse-geocode?key=${environment.awsLocationApiKey}`, payload, this.context)
      .pipe(
        map((response) => response.ResultItems),
        map((items) => items.filter((item) => item.Address?.Country?.Name === ALLOWED_COUNTRY)),
        map((items) => items.map((item) => this.buildAddressLabelFull(item)))
      )
  }

  // get places by query string
  searchAddressWithQuery(query: string): Observable<AddressSelectItem[]> {
    const payload = this.buildSearchRequest(20, { QueryText: query })
    return this.httpClient
      .post<{
        ResultItems: GeocodingSuggest[]
      }>(`${AWS_MAP_PLACES}/autocomplete?key=${environment.awsLocationApiKey}`, payload, this.context)
      .pipe(
        map((response) => response.ResultItems),
        map((items) =>
          items.map((item) => {
            return { label: this.clearCountryFromLabel(item.Title), placeId: item.PlaceId }
          })
        )
      )
  }

  // get exact place with postal code
  searchAddressWithPostalCode(postalCode: string): Observable<AddressSelectItem[]> {
    const payload = this.buildSearchRequest(1, {
      QueryComponents: {
        PostalCode: postalCode,
      },
    })
    return this.httpClient
      .post<{
        ResultItems: Place[]
      }>(`${AWS_MAP_PLACES}/geocode?key=${environment.awsLocationApiKey}`, payload, this.context)
      .pipe(
        map((response) => response.ResultItems),
        map((items) =>
          items.map((item) => {
            return { label: this.clearCountryFromLabel(item.Address.Label), placeId: item.PlaceId }
          })
        )
      )
  }

  // get coordinates by place ID
  getPlaceById(placeId: string): Observable<Place> {
    return this.httpClient.get<Place>(
      `${AWS_MAP_PLACES}/place/${placeId}?key=${environment.awsLocationApiKey}&intended-use=${INTENDED_USE}&language=${SEARCH_LANGUAGE}`,
      this.context
    )
  }

  private buildSearchRequest(maxResult: number, params: Record<string, unknown>) {
    return {
      Filter: {
        IncludeCountries: [ALLOWED_COUNTRY_CODE],
        BoundingBox: [LONGITUDE_RANGE.min, LONGITUDE_RANGE.min, LONGITUDE_RANGE.max, LATITUDE_RANGE.max],
      },
      MaxResults: maxResult,
      Language: SEARCH_LANGUAGE,
      ...params,
    }
  }

  private clearCountryFromLabel(label: string) {
    return label
      .replace(new RegExp(`,?\\s*${ALLOWED_COUNTRY}$`), '')
      .replace(new RegExp(`${ALLOWED_COUNTRY},?\\s*`), '')
      .trim()
  }

  private buildAddressLabelFull(item: Place): AddressSelectItem {
    const street = `${item.Address.Street || ''} ${item.Address.AddressNumber || ''}`
    const city = `${item.Address.PostalCode} ${item.Address.Locality || ''}`
    const label = [street, city]
      .map((text) => text.trim())
      .filter((text) => text?.length)
      .join(', ')
      .trim()
    return { label: label, placeId: item.PlaceId }
  }
}
